import React, { useState, useEffect, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Career } from "../Types/Types";
import FormActionButtons from "../Assets/FormActionButtons";
import LanguageTabs, { FormLang } from "../Assets/LanguageTabs";
import { useTranslation } from "react-i18next";

type EnDepartment =
  | "Graphic Design"
  | "R&D"
  | "Pharmaceutical Quality Control"
  | "Pharmaceutical Quality Assurance"
  | "Pharmaceutical Microbiology"
  | "Pharmaceutical Regulatory Affairs"
  | "Maintenance and Engineering"
  | "MANUFACTURE"
  | "Storage"
  | "Sales"
  | "Pharmaceutical Procurement"
  | "Finance"
  | "SECURITY";

const departmentMap = {
  enToMk: {
    "Graphic Design": "Графички дизајн",
    "R&D": "Истражување и развој",
    "Pharmaceutical Quality Control": "Контрола на квалитет",
    "Pharmaceutical Quality Assurance": "Обезбедување на квалитет",
    "Pharmaceutical Microbiology": "Фармацевтска микробиологија",
    "Pharmaceutical Regulatory Affairs": "Регулаторни работи",
    "Maintenance and Engineering": "Одржување и инженеринг",
    "MANUFACTURE": "Производство",
    "Storage": "Магацин",
    "Sales": "Продажба",
    "Pharmaceutical Procurement": "Набавка",
    "Finance": "Финансии",
    "SECURITY": "Обезбедување",
  } as Record<EnDepartment, string>,
  enToSq: {
    "Graphic Design": "Dizajn grafik",
    "R&D": "Hulumtim dhe zhvillim",
    "Pharmaceutical Quality Control": "Kontrolli i cilësisë farmaceutike",
    "Pharmaceutical Quality Assurance": "Sigurimi i cilësisë farmaceutike",
    "Pharmaceutical Microbiology": "Mikrobiologjia farmaceutike",
    "Pharmaceutical Regulatory Affairs": "Çështjet rregullatore farmaceutike",
    "Maintenance and Engineering": "Mirëmbajtja dhe inxhinieria",
    "MANUFACTURE": "Prodhimi",
    "Storage": "Magazina",
    "Sales": "Shitja",
    "Pharmaceutical Procurement": "Furnizimi farmaceutik",
    "Finance": "Financa",
    "SECURITY": "Siguria",
  } as Record<EnDepartment, string>,
  mkToEn: {} as Record<string, EnDepartment>,
  sqToEn: {} as Record<string, EnDepartment>,
};
for (const [en, mk] of Object.entries(departmentMap.enToMk)) {
  departmentMap.mkToEn[mk] = en as EnDepartment;
}
for (const [en, sq] of Object.entries(departmentMap.enToSq)) {
  departmentMap.sqToEn[sq] = en as EnDepartment;
}

interface CareerEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (id: string, formData: object) => Promise<void>;
  initialData: Career;
  lang: string;
}

type Multi = { en: string; mk: string; sq: string };

const toMulti = (field: { [key: string]: string } | undefined): Multi => ({
  en: field?.en || "",
  mk: field?.mk || "",
  sq: field?.sq || "",
});

const CareerEditModal: React.FC<CareerEditModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
}) => {
  const { t } = useTranslation();
  const [activeLang, setActiveLang] = useState<FormLang>("en");
  const [vacancy, setVacancy] = useState<Multi>(toMulti(initialData.vacancy));
  const [shortDescription, setShortDescription] = useState<Multi>(toMulti(initialData.shortDescription));
  const [description, setDescription] = useState<Multi>(toMulti(initialData.description));
  const [department, setDepartment] = useState<{ en: EnDepartment | ""; mk: string; sq: string }>({ en: "", mk: "", sq: "" });
  const [active, setActive] = useState<{ en: boolean; mk: boolean; sq: boolean }>({ en: true, mk: true, sq: true });
  const quillRef = useRef<ReactQuill | null>(null);

  useEffect(() => {
    setVacancy(toMulti(initialData.vacancy));
    setShortDescription(toMulti(initialData.shortDescription));
    setDescription(toMulti(initialData.description));

    const dept = (initialData.department || {}) as { [key: string]: string };
    const deptEn = (dept.en as EnDepartment) || (dept.mk ? departmentMap.mkToEn[dept.mk] : "") || "";
    setDepartment({
      en: deptEn,
      mk: dept.mk || (deptEn ? departmentMap.enToMk[deptEn as EnDepartment] : ""),
      sq: dept.sq || (deptEn ? departmentMap.enToSq[deptEn as EnDepartment] : ""),
    });

    const act = initialData.active as { en?: boolean; mk?: boolean; sq?: boolean } | boolean;
    if (typeof act === "object" && act !== null) {
      setActive({ en: !!act.en, mk: !!act.mk, sq: act.sq !== undefined ? !!act.sq : false });
    } else {
      setActive({ en: !!act, mk: !!act, sq: !!act });
    }
  }, [initialData]);

  const setField = (
    setter: React.Dispatch<React.SetStateAction<Multi>>,
    value: string
  ) => setter((prev) => ({ ...prev, [activeLang]: value }));

  const selectDepartmentByEn = (enValue: EnDepartment | "") => {
    setDepartment({
      en: enValue,
      mk: enValue ? departmentMap.enToMk[enValue] : "",
      sq: enValue ? departmentMap.enToSq[enValue] : "",
    });
  };

  const onDepartmentChange = (value: string) => {
    let enValue: EnDepartment | "" = "";
    if (activeLang === "en") enValue = value as EnDepartment;
    else if (activeLang === "mk") enValue = departmentMap.mkToEn[value] || "";
    else enValue = departmentMap.sqToEn[value] || "";
    selectDepartmentByEn(enValue);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(initialData._id, {
      vacancy,
      description,
      shortDescription,
      department,
      active,
    });
    onClose();
  };

  const isQuillEmpty = (html: string) =>
    html.replace(/<(.|\n)*?>/g, "").replace(/&nbsp;/g, "").trim().length === 0;

  if (!isOpen) return null;

  const langSuffix = activeLang.toUpperCase();

  const deptOptions =
    activeLang === "en"
      ? Object.keys(departmentMap.enToMk)
      : activeLang === "mk"
      ? Object.values(departmentMap.enToMk)
      : Object.values(departmentMap.enToSq);

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-3xl relative max-h-[90vh] overflow-y-auto font-montserrat">
        <h2 className="text-xl font-semibold mb-4 text-center text-brand-800">{t(`forms.editCareer`)}</h2>

        <LanguageTabs active={activeLang} onChange={setActiveLang} />

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block font-medium mb-1">{t(`forms.vacancy`)} ({langSuffix})</label>
            <input
              type="text"
              value={vacancy[activeLang]}
              onChange={(e) => setField(setVacancy, e.target.value)}
              className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400 transition-colors"
              required={activeLang !== "sq"}
            />
          </div>

          <div>
            <label className="block font-medium mb-1">{t(`forms.shortDescription`)} ({langSuffix})</label>
            <input
              type="text"
              value={shortDescription[activeLang]}
              onChange={(e) => setField(setShortDescription, e.target.value)}
              className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400 transition-colors"
              required={activeLang !== "sq"}
            />
          </div>

          <div>
            <label className="block font-medium mb-1">{t(`forms.description`)} ({langSuffix})</label>
            <ReactQuill
              ref={quillRef}
              key={`desc-${activeLang}`}
              value={description[activeLang]}
              onChange={(value) => setField(setDescription, value)}
              theme="snow"
              className="h-64"
            />
          </div>
          <br />
          <br />

          <div>
            <label className="block font-medium mb-1">{t(`forms.department`)} ({langSuffix})</label>
            <select
              value={department[activeLang]}
              onChange={(e) => onDepartmentChange(e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="">—</option>
              {deptOptions.map((dep) => (
                <option key={dep} value={dep}>{dep}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <label className="font-medium">{t(`forms.active`)} ({langSuffix})</label>
            <input
              type="checkbox"
              checked={active[activeLang]}
              onChange={() => setActive((prev) => ({ ...prev, [activeLang]: !prev[activeLang] }))}
            />
          </div>

          <FormActionButtons
            onClose={onClose}
            onSubmit={handleSubmit}
            edit={true}
            disabled={!vacancy.en || !shortDescription.en || isQuillEmpty(description.en)}
          />
        </form>
      </div>
    </div>
  );
};

export default CareerEditModal;
